import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessagePayload } from '../../../hooks/useChatSocket';
import { ChatConversation } from '../../../context/ChatContext';

interface ClosedSummaryCardProps {
    message: ChatMessagePayload;
    activeConversation?: ChatConversation;
}

export const ClosedSummaryCard: React.FC<ClosedSummaryCardProps> = ({ message, activeConversation }) => {
    const { t } = useTranslation();
    const event = message.metadata?.event || 'closed';
    const reason = message.metadata?.reason || '';

    // Professional's name: comes from otherUser when the patient sees this card, or the current user when the professional sees it.
    // The ClosedSummaryCard does not have access to useAuthContext by design to keep it simple.
    // We use activeConversation.otherUser.firstName as the default professional name label.
    const professionalName = activeConversation?.otherUser?.firstName || '';

    if (event === 'expired') {
        return (
            <article className="system-message system-message--expired">
                <div className="system-title system-title--centered">
                    <span className="status-icon status-icon--large">⌛</span>
                </div>
                <div className="system-title-copy system-title-copy--centered">
                    <strong>{t('Chat.ClosedCard.requestExpiredTitle', 'Solicitud vencida')}</strong>
                    <span>{t('Chat.ClosedCard.requestExpiredDesc', 'El plazo de 12 horas terminó sin selección. Las opciones quedaron liberadas y ambas partes fueron notificadas.')}</span>
                </div>
                <p className="system-foot">{t('Chat.ClosedCard.backupOnlyFoot', 'Hilo cerrado · disponible únicamente como respaldo')}</p>
            </article>
        );
    }

    if (event === 'payment_expired') {
        return (
            <article className="system-message system-message--expired">
                <div className="system-title system-title--centered">
                    <span className="status-icon status-icon--large">⌛</span>
                </div>
                <div className="system-title-copy system-title-copy--centered">
                    <strong>{t('Chat.ClosedCard.paymentExpiredTitle', 'Pago vencido')}</strong>
                    <span>{t('Chat.ClosedCard.paymentExpiredDesc', 'El plazo de 30 minutos para el pago terminó sin éxito. Las opciones quedaron liberadas y ambas partes fueron notificadas.')}</span>
                </div>
                <p className="system-foot">{t('Chat.ClosedCard.backupOnlyFoot', 'Hilo cerrado · disponible únicamente como respaldo')}</p>
            </article>
        );
    }

    if (event === 'request_rejected') {
        const defaultReason = t('Chat.ClosedCard.defaultRejectReason', 'No dispongo de un horario compatible con la solicitud.');
        const rejectReason = reason || defaultReason;
        const reasonLabel = professionalName 
            ? t('Chat.ClosedCard.reasonByProfessional', 'Motivo de {{name}}', { name: professionalName }) 
            : t('Chat.ClosedCard.reasonTitle', 'Motivo');

        return (
            <article className="system-message system-message--rejected">
                <div className="system-title system-title--centered">
                    <span className="status-icon status-icon--large history">×</span>
                </div>
                <div className="system-title-copy system-title-copy--centered">
                    <strong>{t('Chat.ClosedCard.requestRejectedTitle', 'Solicitud no aceptada')}</strong>
                    <span>
                        {reasonLabel}: &ldquo;{rejectReason}&rdquo; {t('Chat.ClosedCard.requestRejectedNotified', 'Ambas partes fueron notificadas.')}
                    </span>
                </div>
                <p className="system-foot">{t('Chat.ClosedCard.backupOnlyFoot', 'Hilo cerrado · disponible únicamente como respaldo')}</p>
            </article>
        );
    }

    if (event === 'cancel_request') {
        return (
            <article className="system-message">
                <div className="system-title">
                    <span className="status-icon history">×</span>
                    <span className="system-title-copy">
                        <strong>{t('Chat.ClosedCard.requestCancelledTitle', 'Solicitud cancelada')}</strong>
                        <span>{t('Chat.ClosedCard.requestCancelledDesc', 'La solicitud fue cancelada por el usuario.')}</span>
                    </span>
                </div>
                <p className="system-foot">{t('Chat.ClosedCard.readOnlyFoot', 'Este hilo está cerrado y es de solo lectura.')}</p>
            </article>
        );
    }

    // Default: confirmed
    return (
        <article className="system-message">
            <div className="system-title">
                <span className="status-icon">✓</span>
                <span className="system-title-copy">
                    <strong>{t('Chat.ClosedCard.bookingConfirmedTitle', 'Reserva confirmada')}</strong>
                    <span>{t('Chat.ClosedCard.bookingConfirmedDesc', 'El pago ha sido acreditado y la reserva está confirmada. Ambas partes fueron notificadas.')}</span>
                </span>
            </div>
            <p className="system-foot">{t('Chat.ClosedCard.readOnlyFoot', 'Este hilo está cerrado y es de solo lectura.')}</p>
        </article>
    );
};
